import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FeedbackResultDoc } from '@/dao/firestore/FeedbackResultsRepository';
import { PersonaDoc } from '@/dao/firestore/PersonasRepository';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, CartesianGrid, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Users, Star, MessageSquare, MapPin, DollarSign, Briefcase } from 'lucide-react';
import { ScreeningNotesAnalyticsModal } from './ScreeningNotesAnalyticsModal';
import { AudienceCharts } from './AudienceCharts';

interface ResultsAnalyticsModalProps {
  open: boolean;
  onClose: () => void;
  results: FeedbackResultDoc[];
  personas?: PersonaDoc[];
  useNewAnalytics?: boolean;
}

const COLORS = [
  'hsl(var(--primary))', 
  'hsl(var(--brand-accent))', 
  'hsl(var(--brand-warm))', 
  'hsl(var(--success))', 
  'hsl(var(--warning))', 
  'hsl(270 85% 65%)', 
  'hsl(330 85% 65%)', 
  'hsl(45 93% 47%)'
];

export function ResultsAnalyticsModal({ 
  open, 
  onClose, 
  results, 
  personas = [], 
  useNewAnalytics = false 
}: ResultsAnalyticsModalProps) {
  const { t } = useTranslation();

  // Use new analytics modal if requested and personas are available
  if (useNewAnalytics && personas.length > 0) {
    return (
      <ScreeningNotesAnalyticsModal
        open={open}
        onClose={onClose}
        results={results}
        personas={personas}
      />
    );
  }

  // Results analytics data
  const totalResults = results.length;
  const avgRating = results.reduce((sum, r) => sum + (r.rating || 0), 0) / totalResults || 0;
  
  // Personal feedback ratings for radar chart
  const personalFeedbackResults = results.filter(r => r.agent === 'personal-feedback');
  const categoryRatings = personalFeedbackResults.length > 0 ? {
    visual: personalFeedbackResults.reduce((sum, r) => sum + (r.visual?.rating || 0), 0) / personalFeedbackResults.length,
    content: personalFeedbackResults.reduce((sum, r) => sum + (r.content?.rating || 0), 0) / personalFeedbackResults.length,
    script: personalFeedbackResults.reduce((sum, r) => sum + (r.script?.rating || 0), 0) / personalFeedbackResults.length,
    other: personalFeedbackResults.reduce((sum, r) => sum + (r.other?.rating || 0), 0) / personalFeedbackResults.length,
    global: avgRating
  } : null;

  // Radar chart data
  const radarData = categoryRatings ? [
    { subject: 'Visual', A: categoryRatings.visual, fullMark: 5 },
    { subject: 'Content', A: categoryRatings.content, fullMark: 5 },
    { subject: 'Script', A: categoryRatings.script, fullMark: 5 },
    { subject: 'Other', A: categoryRatings.other, fullMark: 5 },
    { subject: 'Global', A: categoryRatings.global, fullMark: 5 }
  ] : [];

  // Sentiment distribution
  const sentimentData = [
    { name: 'Positive', value: results.filter(r => r.sentiment === 'positive').length, color: 'hsl(var(--chart-1))' },
    { name: 'Neutral', value: results.filter(r => r.sentiment === 'neutral').length, color: 'hsl(var(--chart-2))' },
    { name: 'Negative', value: results.filter(r => r.sentiment === 'negative').length, color: 'hsl(var(--chart-3))' }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
           Análisis de Resultados
          </DialogTitle>
        </DialogHeader>

        {totalResults === 0 && personas.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              No hay datos disponibles para el análisis
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Audiencia Section - Using AudienceCharts component */}
            {personas.length > 0 && (
              <AudienceCharts 
                personas={personas} 
                showHeader={true} 
                showOverview={true} 
                mode="slides" 
              />
            )}

            {/* Results Section */}
            {totalResults > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  <h2 className="text-xl font-semibold">Análisis de Feedback</h2>
                </div>

                {/* Results Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Total Feedback</span>
                      </div>
                      <div className="text-2xl font-bold">{totalResults}</div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Puntuación Media</span>
                      </div>
                      <div className="text-2xl font-bold">{avgRating.toFixed(1)}/5</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        {sentimentData.find(s => s.name === 'Positive')!.value > sentimentData.find(s => s.name === 'Negative')!.value ? 
                          <TrendingUp className="h-4 w-4 text-green-500" /> : 
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        }
                        <span className="text-sm text-muted-foreground">Sentimiento</span>
                      </div>
                      <div className="text-2xl font-bold">
                        {sentimentData.find(s => s.value === Math.max(...sentimentData.map(d => d.value)))?.name}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Comentarios</span>
                      </div>
                      <div className="text-2xl font-bold">
                        {results.filter(r => r.comment && r.comment.trim()).length}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Sentiment Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Distribución de Sentimientos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={sentimentData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {sentimentData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Radar Chart for Professional Feedback */}
                  {radarData.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Valoraciones por Categoría</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[200px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subject" />
                              <PolarRadiusAxis angle={90} domain={[0, 5]} />
                              <Radar
                                name="Puntuación"
                                dataKey="A"
                                stroke="hsl(var(--primary))"
                                fill="hsl(var(--primary))"
                                fillOpacity={0.3}
                              />
                              <Tooltip />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}